import { DialogsStateProvider } from '../components/widgets/dialog';
import { clone } from 'lodash';
import React, { createContext, FunctionComponent, useContext, useReducer } from 'react';
import { graphql, useLazyLoadQuery } from 'react-relay';
import { appStateAdminUserQuery } from './__generated__/appStateAdminUserQuery.graphql';
import type { appStateQuery as AppStateQueryType } from './__generated__/appStateQuery.graphql';
import { BackofficeUiPreferencesProvider } from './backofficeUiPreferences';
import useShouldAcceptTermsAndConditionsDialog from './hooks/useShouldAcceptTermsAndConditionsDialog';

// Define the GraphQL query that can be used across the app
export const appStateQueryDef = graphql`
    query appStateQuery($skipRequest: Boolean!) {
        ...appSubscriptionCheck @alias @skip(if: $skipRequest)
        ...defaultLayoutContainerPriceFormatterSupplierFragment @alias @skip(if: $skipRequest)
        ...defaultLayoutContainerSellerLastUsedToolFragment @alias @skip(if: $skipRequest)
        ...useAccountDataFragment @alias @skip(if: $skipRequest)
        ...profitWellFragment @alias @skip(if: $skipRequest)
        ...usetifulFragment @alias @skip(if: $skipRequest)
        ...tagManagerFragment @alias @skip(if: $skipRequest)
        ...useShouldAcceptTermsAndConditionsDialogFragment @alias @skip(if: $skipRequest)
        # Qui e non in una query sua: il tema deve esserci prima del primo disegno.
        ...backofficeUiPreferencesFragment @alias @skip(if: $skipRequest)
    }
`;

interface AppState {
    dialogs: { id: string; dialog: React.ReactNode }[];
    showLoading: boolean;
    priceFormatter: Intl.NumberFormat;
    loadedScripts: string[]; // Changed from isBabylonLoaded to loadedScripts
    user: {
        id: number;
        name: string;
    };
    lastUsedTool: number;
    termsAccepted: boolean;
}

type AppStateReducer = (state: AppState, action: any) => AppState;
type AppStateDispatch = (action: any) => void;

const initialState: AppState = {
    dialogs: [],
    showLoading: false,
    loadedScripts: [], // Changed from isBabylonLoaded to loadedScripts
    priceFormatter: new Intl.NumberFormat(),
    user: { id: 0, name: '' },
    lastUsedTool: 0,
    termsAccepted: false
};

const context = createContext<{ state: AppState; dispatch: AppStateDispatch; queryData: AppStateQueryType['response'] | null }>({
    state: initialState,
    dispatch: () => new Error('Component not wrapped in state provider.'),
    queryData: null
});

const reducer = (state: AppState, action: any) => {
    const newState = clone(state);

    switch (action.type) {
        case 'PRICE_FORMATTER':
            newState.priceFormatter = action.priceFormatter;
            break;
        case 'LAST_USED_TOOL':
            newState.lastUsedTool = action.lastUsedTool;
            break;
        case 'SHOW_LOADING':
            newState.showLoading = action.showLoading;
            break;
        case 'TERMS_ACCEPTED':
            newState.termsAccepted = action.termsAccepted;
            break;
        case 'SHOW_DIALOG':
            {
                const id = (action.id || new Date().getUTCMilliseconds()).toString();

                if (!newState.dialogs.find((x) => x.id === id))
                    newState.dialogs.push({
                        id,
                        dialog: React.cloneElement(action.dialog, { key: id })
                    });
            }
            break;
        case 'CLOSE_DIALOG':
            newState.dialogs = newState.dialogs.filter((x) => {
                return x.id != action.dialogId;
            });
            break;
        case 'ADD_LOADED_SCRIPT':
            if (!newState.loadedScripts.includes(action.scriptUrl)) {
                newState.loadedScripts = [...newState.loadedScripts, action.scriptUrl];
            }
            break;
        default:
            throw new Error();
    }

    return newState;
};

const TermsAndConditionsHandler: FunctionComponent = () => {
    const { queryData, dispatch } = useAppState();

    useShouldAcceptTermsAndConditionsDialog(queryData?.useShouldAcceptTermsAndConditionsDialogFragment ?? null, () => {
        dispatch({ type: 'TERMS_ACCEPTED', termsAccepted: true });
    });

    return null;
};

const AppStateProvider: FunctionComponent<{ children?: React.ReactNode }> = ({ children }) => {
    const [state, dispatch] = useReducer(reducer, initialState);

    // Check if user is admin
    const result = useLazyLoadQuery<appStateAdminUserQuery>(
        graphql`
            query appStateAdminUserQuery {
                isAdminUser
                seller {
                    sellerID
                }
                currentIAMUser {
                    isIAMUser
                    permissions
                }
            }
        `,
        {},
        { fetchPolicy: 'network-only' }
    );

    // Execute the query in the provider, skip fragments based on user type
    const queryData = useLazyLoadQuery<AppStateQueryType>(appStateQueryDef, {
        skipRequest: result.isAdminUser || !result.seller?.sellerID
    });

    return (
        <BackofficeUiPreferencesProvider fragmentRef={queryData.backofficeUiPreferencesFragment ?? null}>
            <DialogsStateProvider>
                <context.Provider value={{ state, dispatch, queryData }}>
                    {!result.isAdminUser && result.seller?.sellerID && <TermsAndConditionsHandler />}
                    {children}
                </context.Provider>
            </DialogsStateProvider>
        </BackofficeUiPreferencesProvider>
    );
};

const useAppState = function () {
    return useContext(context);
};

export { AppStateProvider, useAppState };
